import { isNavigationFailure, NavigationFailureType, type Router } from "vue-router";
import { BaseUrl } from "../utils/constants/serverName";

/**
 * Remembers the page a user asked for while logged out, so the login form can send
 * them there instead of dropping them on the home page.
 *
 * The value lives in `sessionStorage` because SSO leaves the app entirely (full page
 * navigation to the identity provider and back) — in-memory state would not survive
 * that round trip, and the `POST /sso` endpoint has no slot to carry a return URL.
 *
 * This module deliberately does not import the router: it is used from the user store,
 * and an import cycle would break the store's initialisation. Callers that need to
 * navigate pass their router instance in.
 */

const STORAGE_KEY = "postLoginReturnUrl";
const RETURN_URL_PARAM = "return_url";

export const DEFAULT_POST_LOGIN_TARGET = "/home";

/**
 * Paths that are never worth returning to: they are either the default target anyway,
 * or landing spots the app itself redirects to when a session ends — remembering those
 * would make login bounce the user straight back into the state they just escaped.
 */
const DENYLIST = ["/", "/home", "/llm", "/access-denied", "/changePassword", "/settings/password"];

const isSafePath = (path: string) => {
  if (!path.startsWith("/")) {
    return false;
  }
  // "//host" and "/\host" are protocol-relative URLs that browsers resolve off-site,
  // not in-app routes.
  if (path.startsWith("//") || path.startsWith("/\\")) {
    return false;
  }
  const [pathname] = path.split(/[?#]/);
  return !DENYLIST.includes(pathname);
};

/** `window.location.pathname` carries the router base; route paths do not. */
const stripRouterBase = (pathname: string) => {
  const base = BaseUrl.endsWith("/") ? BaseUrl.slice(0, -1) : BaseUrl;
  if (base.length > 0 && pathname.startsWith(base)) {
    return pathname.slice(base.length) || "/";
  }
  return pathname;
};

const currentLocationPath = () => `${stripRouterBase(window.location.pathname)}${window.location.search}${window.location.hash}`;

/** An explicit `?return_url=` wins, so links from outside the app can point at the login page. */
const requestedReturnUrl = () => {
  const requested = new URLSearchParams(window.location.search).get(RETURN_URL_PARAM);
  if (requested == null) {
    return null;
  }
  return isSafePath(requested) ? requested : null;
};

export const captureReturnUrl = () => {
  const target = requestedReturnUrl() ?? currentLocationPath();
  if (!isSafePath(target)) {
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, target);
  } catch (e) {
    console.warn("Unable to remember the requested page", e);
  }
};

export const clearReturnUrl = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn("Unable to forget the requested page", e);
  }
};

/**
 * Reads and immediately discards the stored target. Consuming it up front is what keeps
 * a broken deep link from looping: at worst it costs one failed navigation per login.
 */
const consumeReturnUrl = () => {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn("Unable to read the requested page", e);
    return null;
  }
  if (stored == null || !isSafePath(stored)) {
    return null;
  }
  return stored;
};

/** Sends a freshly authenticated user to the page they originally asked for. */
export const goToPostLoginTarget = async (router: Router) => {
  const target = consumeReturnUrl();
  if (target == null) {
    return router.replace(DEFAULT_POST_LOGIN_TARGET);
  }

  // A URL that no longer matches a route would land on the not-found page.
  if (router.resolve(target).matched.some((record) => record.name === "not-found")) {
    return router.replace(DEFAULT_POST_LOGIN_TARGET);
  }

  try {
    const failure = await router.replace(target);
    // Only an outright rejection by a guard falls back; a cancelled navigation means
    // something else (e.g. the access-denied redirect) has taken over already.
    if (isNavigationFailure(failure, NavigationFailureType.aborted)) {
      await router.replace(DEFAULT_POST_LOGIN_TARGET);
    }
  } catch (e) {
    console.warn("Unable to return to the requested page", e);
    await router.replace(DEFAULT_POST_LOGIN_TARGET);
  }
};
